import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { BarChart2, TrendingUp, Users, Activity, Download, Building2, Star, Clock, Calendar, Loader2 } from 'lucide-react';
import { t } from '../../hooks/useAdminTheme';
import AnimatedNumber from '../../components/shared/AnimatedNumber';
import { apiFetch } from '../../services/cloudflareService';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid, AreaChart, Area } from 'recharts';

interface HospitalMetrics {
    patients: number;
    rooms: number;
    screensOnline: number;
    screensOffline: number;
    avgRating: number | null;
    totalFeedback: number;
    todaySurgeries: number;
}

interface EnrichedHospital {
    id: string;
    name: string;
    handle: string;
    logoUrl?: string;
    status: 'active' | 'inactive';
    metrics: HospitalMetrics;
    preferences?: any;
}

interface HospitalBreakdown {
    id: string;
    name: string;
    handle: string;
    logoUrl: string | null;
    surgeries: number;
    avgDurationMin: number;
    activePatients: number;
    avgRating: number | null;
    totalFeedback: number;
    screensOnline: number;
    screensOffline: number;
}

interface AnalyticsData {
    aggregate: { totalSurgeries: number; avgPerDay: number; avgDurationMin: number; totalHospitals: number };
    hospitalBreakdown: HospitalBreakdown[];
    dailyTrend: { date: string; surgeries: number }[];
    dateRange: { startDate: string; endDate: string };
}

type DatePreset = '1d' | '7d' | '30d' | '90d' | 'custom';

interface GroupAnalyticsProps {
    hospitals: EnrichedHospital[];
    groupHandle: string;
}

const GroupAnalytics: React.FC<GroupAnalyticsProps> = ({ hospitals, groupHandle }) => {
    const premiumCardClass = "bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_3px_rgba(0,0,0,0.04)]";

    const getLocalDate = (d: Date = new Date()) => {
        return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    };

    // Date range state
    const [datePreset, setDatePreset] = useState<DatePreset>('1d');
    const [customStart, setCustomStart] = useState(getLocalDate());
    const [customEnd, setCustomEnd] = useState(getLocalDate());

    // API data state
    const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
    const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);

    const getDateRange = useCallback((): { startDate: string; endDate: string } => {
        const now = new Date();
        const end = getLocalDate(now);
        if (datePreset === 'custom' && customStart && customEnd) {
            return { startDate: customStart, endDate: customEnd };
        }
        const days = datePreset === '1d' ? 0 : datePreset === '30d' ? 30 : datePreset === '90d' ? 90 : 7;
        const start = new Date(now);
        start.setDate(start.getDate() - days);
        return { startDate: getLocalDate(start), endDate: end };
    }, [datePreset, customStart, customEnd]);

    const fetchAnalytics = useCallback(async () => {
        if (!groupHandle) return;
        setIsLoadingAnalytics(true);
        try {
            const { startDate, endDate } = getDateRange();
            const res = await apiFetch(`/api/groups/${groupHandle}/analytics?startDate=${startDate}&endDate=${endDate}`, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                if (data.success) setAnalyticsData(data);
            }
        } catch (e) {
            console.error('[GroupAnalytics] Fetch error:', e);
        } finally {
            setIsLoadingAnalytics(false);
        }
    }, [groupHandle, getDateRange]);

    useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

    // Fallback values from props (used before API data loads)
    const totalSurgeries = analyticsData?.aggregate.totalSurgeries ?? hospitals.reduce((sum, h) => sum + h.metrics.todaySurgeries, 0);
    const totalPatients = hospitals.reduce((sum, h) => sum + h.metrics.patients, 0);
    const totalRooms = hospitals.reduce((sum, h) => sum + h.metrics.rooms, 0);
    const avgPerDay = analyticsData?.aggregate.avgPerDay ?? 0;
    const avgDuration = analyticsData?.aggregate.avgDurationMin ?? 0;

    const activeHospitals = hospitals.filter(h => h.metrics.todaySurgeries > 0).length;

    // Chart Data
    const chartData = useMemo(() => {
        return [...hospitals]
            .sort((a, b) => b.metrics.todaySurgeries - a.metrics.todaySurgeries)
            .map(h => ({
                name: h.name.replace(/Hospital |Clínica |Centro Médico /i, '').substring(0, 15),
                fullName: h.name,
                surgeries: h.metrics.todaySurgeries,
                patients: h.metrics.patients
            }));
    }, [hospitals]);

    const exportToCSV = () => {
        const headers = ['Sucursal', 'Cirugías', 'Duración Promedio', 'Rating', 'Reseñas', 'Pantallas Online', 'Pantallas Offline', 'Pacientes Activos'];
        const rows = (analyticsData?.hospitalBreakdown || []).map(h => [
            h.name,
            h.surgeries,
            h.avgDurationMin,
            h.avgRating || 0,
            h.totalFeedback,
            h.screensOnline,
            h.screensOffline,
            h.activePatients
        ]);
        const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `analytics_${analyticsData?.dateRange?.startDate || 'export'}_to_${analyticsData?.dateRange?.endDate || 'export'}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <motion.div
            initial="hidden"
            animate="show"
            variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06 } } }}
            className="space-y-6"
        >
            <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <p className={`text-[10px] font-bold uppercase tracking-[0.15em] ${t.textMuted} mb-1`}>Inteligencia de Negocio</p>
                    <h2 className={`text-xl font-black ${t.textWhite} tracking-tight`}>Corporate Analytics</h2>
                    <p className={`text-xs ${t.textMuted} font-medium mt-1`}>
                        {analyticsData?.dateRange
                            ? `${analyticsData.dateRange.startDate} → ${analyticsData.dateRange.endDate}`
                            : `Rendimiento global de ${hospitals.length} sucursales`
                        }
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {/* Date Presets */}
                    {(['1d', '7d', '30d', '90d'] as DatePreset[]).map(preset => (
                        <button
                            key={preset}
                            onClick={() => setDatePreset(preset)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 ${
                                datePreset === preset
                                    ? 'bg-indigo-600 text-white shadow-sm'
                                    : 'bg-white border border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700'
                            }`}
                        >
                            {preset === '1d' ? 'Hoy' : preset === '7d' ? '7 días' : preset === '30d' ? '30 días' : '90 días'}
                        </button>
                    ))}
                    
                    {/* Custom Date Picker */}
                    <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1 ml-2">
                        <Calendar className="w-3.5 h-3.5 text-slate-400" />
                        <input 
                            type="date" 
                            className="text-xs text-slate-600 bg-transparent border-none outline-none cursor-pointer focus:ring-0 p-0 m-0 w-24"
                            value={customStart}
                            onChange={(e) => { setCustomStart(e.target.value); setDatePreset('custom'); }}
                            max={customEnd}
                        />
                        <span className="text-slate-300 text-xs">-</span>
                        <input 
                            type="date" 
                            className="text-xs text-slate-600 bg-transparent border-none outline-none cursor-pointer focus:ring-0 p-0 m-0 w-24"
                            value={customEnd}
                            onChange={(e) => { setCustomEnd(e.target.value); setDatePreset('custom'); }}
                            min={customStart}
                            max={getLocalDate()}
                        />
                    </div>

                    {isLoadingAnalytics && <Loader2 className="w-4 h-4 text-indigo-500 animate-spin ml-2" />}
                    
                    <button
                        onClick={exportToCSV}
                        className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg transition-all bg-white border border-slate-200 hover:border-slate-300 text-slate-500 hover:text-slate-700 active:scale-95 ml-auto sm:ml-2"
                    >
                        <Download className="w-3.5 h-3.5" />
                        CSV
                    </button>
                </div>
            </motion.div>

            {/* KPI Grid */}
            <motion.div variants={{ hidden: { opacity: 0 }, show: { opacity: 1 } }} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }} className={`${premiumCardClass} p-6`}>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100">
                            <Activity className="w-5 h-5" />
                        </div>
                        <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-[0.15em]`}>Cirugías Total</p>
                    </div>
                    <p className={`text-4xl font-black ${t.textWhite} tracking-tighter`}><AnimatedNumber value={totalSurgeries} /></p>
                    {avgPerDay > 0 && <p className="text-xs text-slate-400 mt-1">{avgPerDay}/día promedio</p>}
                </motion.div>

                <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }} className={`${premiumCardClass} p-6`}>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100">
                            <Clock className="w-5 h-5" />
                        </div>
                        <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-[0.15em]`}>Duración Prom.</p>
                    </div>
                    <p className={`text-4xl font-black ${t.textWhite} tracking-tighter`}>{avgDuration}<span className="text-lg text-slate-400 ml-1">min</span></p>
                </motion.div>

                <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }} className={`${premiumCardClass} p-6`}>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 rounded-xl bg-amber-50 text-amber-600 border border-amber-100">
                            <Users className="w-5 h-5" />
                        </div>
                        <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-[0.15em]`}>Pacientes Ahora</p>
                    </div>
                    <p className={`text-4xl font-black ${t.textWhite} tracking-tighter`}><AnimatedNumber value={totalPatients} /></p>
                </motion.div>

                <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }} className={`${premiumCardClass} p-6`}>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 rounded-xl bg-blue-50 text-blue-600 border border-blue-100">
                            <Building2 className="w-5 h-5" />
                        </div>
                        <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-[0.15em]`}>Sucursales</p>
                    </div>
                    <p className={`text-4xl font-black ${t.textWhite} tracking-tighter`}><AnimatedNumber value={hospitals.length} /></p>
                    <p className="text-xs text-slate-400 mt-1">{activeHospitals} activas hoy</p>
                </motion.div>
            </motion.div>

            {/* Trend Chart — Full Width */}
            {analyticsData?.dailyTrend && analyticsData.dailyTrend.length > 1 && (
                <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }} className={`${premiumCardClass} p-6`}>
                    <h3 className={`text-[10px] font-bold uppercase tracking-[0.15em] ${t.textMuted} mb-6 flex items-center gap-2`}>
                        <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
                        Tendencia de Cirugías
                    </h3>
                    <div className="h-[220px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={analyticsData.dailyTrend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.15} />
                                        <stop offset="95%" stopColor="#4f46e5" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                <XAxis
                                    dataKey="date"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 10, fill: '#94a3b8' }}
                                    tickFormatter={(v: string) => { const d = new Date(v + 'T12:00:00'); return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }); }}
                                />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
                                <Tooltip
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '12px' }}
                                    labelFormatter={(v: string) => new Date(v + 'T12:00:00').toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}
                                />
                                <Area type="monotone" dataKey="surgeries" name="Cirugías" stroke="#4f46e5" strokeWidth={2} fill="url(#trendGradient)" dot={false} activeDot={{ r: 4, fill: '#4f46e5' }} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </motion.div>
            )}

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Bar Chart — Surgeries by Hospital */}
                <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }} className={`${premiumCardClass} p-6 lg:col-span-2 flex flex-col`}>
                    <h3 className={`text-[10px] font-bold uppercase tracking-[0.15em] ${t.textMuted} mb-6 flex items-center gap-2`}>
                        <BarChart2 className="w-3.5 h-3.5 text-indigo-500" />
                        Cirugías por Sucursal ({datePreset === '7d' ? '7 días' : datePreset === '30d' ? '30 días' : '90 días'})
                    </h3>
                    <div className="flex-1 min-h-[260px]">
                        {(analyticsData?.hospitalBreakdown || chartData).length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={(analyticsData?.hospitalBreakdown || []).map(h => ({
                                        name: h.name.replace(/Hospital |Clínica |Centro Médico /i, '').substring(0, 15),
                                        surgeries: h.surgeries
                                    })).sort((a, b) => b.surgeries - a.surgeries)}
                                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} dy={10} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
                                    <Tooltip
                                        cursor={{ fill: '#f8fafc' }}
                                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                    />
                                    <Bar dataKey="surgeries" name="Cirugías" radius={[4, 4, 0, 0]}>
                                        {(analyticsData?.hospitalBreakdown || []).sort((a, b) => b.surgeries - a.surgeries).map((_, index) => (
                                            <Cell key={`cell-${index}`} fill={index === 0 ? '#4f46e5' : '#818cf8'} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-slate-400">
                                <Activity className="w-8 h-8 mb-2 opacity-50" />
                                <p className="text-sm font-medium">Sin cirugías en este período</p>
                            </div>
                        )}
                    </div>
                </motion.div>

                {/* Ranking Sidebar */}
                <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }} className={`${premiumCardClass} p-0 overflow-hidden lg:col-span-1 flex flex-col`}>
                    <div className="p-5 pb-3 border-b border-slate-100">
                        <h3 className={`text-[10px] font-bold uppercase tracking-[0.15em] ${t.textMuted} flex items-center gap-2`}>
                            <Star className="w-3.5 h-3.5 text-amber-500" />
                            Ranking por Cirugías
                        </h3>
                    </div>
                    <div className="flex-1 overflow-y-auto max-h-[300px]">
                        {[...(analyticsData?.hospitalBreakdown || [])]
                            .sort((a, b) => b.surgeries - a.surgeries)
                            .map((h, i) => (
                                <div key={h.id} className="flex items-center gap-3 px-5 py-3.5 border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors">
                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-xs ${
                                        i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-slate-200 text-slate-600' : i === 2 ? 'bg-orange-100 text-orange-600' : 'bg-slate-100 text-slate-400'
                                    }`}>
                                        {i + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-bold ${t.textWhite} truncate`}>{h.name}</p>
                                        <p className="text-[11px] text-slate-400 mt-0.5">
                                            {h.avgRating ? <><Star className="w-3 h-3 text-amber-400 fill-amber-400 inline mr-0.5" />{h.avgRating}</> : '—'} · {h.activePatients} activos
                                        </p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <p className={`text-lg font-black ${t.textWhite} tabular-nums`}>{h.surgeries}</p>
                                        <p className="text-[10px] text-slate-400">cirugías</p>
                                    </div>
                                </div>
                            ))}
                        {(!analyticsData?.hospitalBreakdown || analyticsData.hospitalBreakdown.length === 0) && (
                            <div className="p-8 text-center text-slate-400">
                                <p className="text-sm font-medium">Cargando datos...</p>
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>

            {/* Comparison Table */}
            {analyticsData?.hospitalBreakdown && analyticsData.hospitalBreakdown.length > 0 && (
                <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } } }} className={`${premiumCardClass} overflow-hidden`}>
                    <div className="p-5 border-b border-slate-100">
                        <h3 className={`text-[10px] font-bold uppercase tracking-[0.15em] ${t.textMuted} flex items-center gap-2`}>
                            <Building2 className="w-3.5 h-3.5 text-blue-500" />
                            Detalle por Sucursal
                        </h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-slate-50/80 border-b border-slate-100">
                                    <th className="text-left px-5 py-3 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Sucursal</th>
                                    <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Cirugías</th>
                                    <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Duración Prom.</th>
                                    <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Rating</th>
                                    <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Pantallas</th>
                                    <th className="text-right px-5 py-3 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Pac. Activos</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...analyticsData.hospitalBreakdown]
                                    .sort((a, b) => b.surgeries - a.surgeries)
                                    .map(h => (
                                        <tr key={h.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors">
                                            <td className="px-5 py-3.5">
                                                <p className={`font-bold ${t.textWhite}`}>{h.name}</p>
                                            </td>
                                            <td className={`text-right px-4 py-3.5 font-mono font-bold ${t.textWhite}`}>{h.surgeries}</td>
                                            <td className="text-right px-4 py-3.5 font-mono text-slate-500">{h.avgDurationMin > 0 ? `${h.avgDurationMin} min` : '—'}</td>
                                            <td className="text-right px-4 py-3.5">
                                                {h.avgRating ? (
                                                    <span className="inline-flex items-center gap-1 text-slate-600 font-bold">
                                                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" />{h.avgRating}
                                                    </span>
                                                ) : <span className="text-slate-300">—</span>}
                                            </td>
                                            <td className="text-right px-4 py-3.5">
                                                <span className="text-emerald-600 font-bold">{h.screensOnline}</span>
                                                {h.screensOffline > 0 && <span className="text-red-500 text-xs ml-1">/{h.screensOffline}</span>}
                                            </td>
                                            <td className={`text-right px-5 py-3.5 font-mono font-bold ${t.textWhite}`}>{h.activePatients}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </motion.div>
            )}
        </motion.div>
    );
};

export default GroupAnalytics;
